/**
 * Permissions service
 */
import { isEmpty, some } from 'lodash';
import { Injectable, OnDestroy } from '@angular/core';

import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

import { PermissionsProviderService } from './permissions-provider.service';
import { IPermission } from './types';

@Injectable()
export class PermissionsService implements OnDestroy {
  public static readonly defaultAppCode: string = 'xvi';

  public subscription: Subscription;
  public permissions: IPermission[] = [];
  public isPermissionsSetInitialized: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  /**
   * Create Subject and subscribe on permissions changes
   *  in the PermissionsProviderService instance
   *
   */
  public constructor(private permissionsProvider: PermissionsProviderService) {
    this.subscription = this.permissionsProvider.permissions$.subscribe(
      (permissions = []) => {
        this.permissions = permissions;
        this.isPermissionsSetInitialized.next(true);
      },
    );
  }

  public ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * Check permission in the permissions set and return 'true'
   *  when permission exist
   *
   * @return
   */
  public hasPermission(
    permission: string,
    appCode: string = PermissionsService.defaultAppCode,
  ): boolean {
    return this.permissions
      .filter((perm) => Boolean(perm.value))
      .some((perm) => perm.name === permission && perm.app_code === appCode);
  }

  /**
   * Tracks if current user has specified permissions.
   * Initial value and all the changes are returned via Observable
   *
   * @return
   */
  public trackPermissionsChange(
    permissions: string[],
    appCode: string = PermissionsService.defaultAppCode,
  ): Observable<boolean> {
    return this.permissionsProvider.permissions$.pipe(
      filter((val) => {
        return !!val;
      }),
      // true if all permissions are in the permissions$
      map(() =>
        (permissions || []).every((permission) => {
          return this.hasPermission(permission, appCode);
        }),
      ),
      distinctUntilChanged(),
    );
  }

  /**
   * Tracks if current user has in permissions at least one of specified permissions.
   * Initial value and all the changes are returned via Observable
   *
   * @return
   */
  public trackAnyOfPermissionsChange(
    permissions: string[],
    appCode: string = PermissionsService.defaultAppCode,
  ): Observable<boolean> {
    return this.permissionsProvider.permissions$.pipe(
      // true if any of permissions are in the permissions$
      map(() => {
        if (!isEmpty(permissions)) {
          return some(permissions, (permission) => {
            return this.hasPermission(permission, appCode);
          });
        }

        // return true if atleast one permission with matching app code
        return some(
          this.permissions,
          (permission) => permission.app_code === appCode,
        );
      }),
      distinctUntilChanged(),
    );
  }
}
